import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetRepairSlipReportQuery } from '../../../../api/endpoints/repairSlipReport';
import { matchModeToOperator } from '../../../../utils/filterUtils';
import { formatDateForApi } from '../../../../utils/dateUtils';

export const backendColumnMap = {
    isemriNo: 'IsemriNo',
    bayiAdi: 'BayiAdi',
    tamirTarihi: 'TamirTarihi',
    tamirAyi: 'TamirAyi',
    tamirYili: 'TamirYili',
    sasiNo: 'SasiNo',
    model: 'Model',
    musteriOderToplamParca: 'MusteriOderToplamParca',
    karsanGarantiliToplamParca: 'KarsanGarantiliToplamParca',
    musteriOderToplamIscilik: 'MusteriOderToplamIscilik',
    karsanGarantiliToplamIscilik: 'KarsanGarantiliToplamIscilik',
    toplamMoTutar: 'ToplamMoTutar'
};

export const TEXT_FIELDS = ['sasiNo'];

export const DROPDOWN_FIELDS = ['bayiAdi', 'model'];

export const DATE_FIELDS = ['tamirTarihi'];

export const NUMERIC_FIELDS = ['isemriNo', 'tamirAyi', 'tamirYili', 'musteriOderToplamParca', 'karsanGarantiliToplamParca', 'musteriOderToplamIscilik', 'karsanGarantiliToplamIscilik', 'toplamMoTutar'];

export const AMOUNT_FIELDS = ['musteriOderToplamParca', 'karsanGarantiliToplamParca', 'musteriOderToplamIscilik', 'karsanGarantiliToplamIscilik', 'toplamMoTutar'];

const FILTER_OPTIONS_COLUMNS = DROPDOWN_FIELDS.map((field) => backendColumnMap[field]);

const OPERATOR_EQUALS = 5;

const INITIAL_LAZY_PARAMS = { first: 0, rows: 10, page: 1 };

const DEFAULT_SORT_FIELD = 'isemriNo';

const DEFAULT_SORT_ORDER = -1;

const INITIAL_FILTER_STATE = {
    ...TEXT_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...NUMERIC_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...DATE_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...DROPDOWN_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        return acc;
    }, {})
};

const getOperatorValue = (matchMode, fallbackMatchMode) => matchModeToOperator[matchMode] ?? matchModeToOperator[fallbackMatchMode];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || String(value).trim() === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.CONTAINS),
            value: String(value).trim()
        });
    });

    NUMERIC_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || String(value).trim() === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.EQUALS),
            value: String(value).trim()
        });
    });

    DATE_FIELDS.forEach((field) => {
        const formatted = formatDateForApi(filtersState?.[field]);
        if (!formatted) return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.EQUALS),
            value: formatted
        });
    });

    DROPDOWN_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || value === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: OPERATOR_EQUALS,
            value: String(value)
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useRepairSlipReport = () => {
    const [lazyParams, setLazyParams] = useState(INITIAL_LAZY_PARAMS);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
    const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
    const [filterOptions, setFilterOptions] = useState(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            includeFilterOptions: !filterOptionsLoaded,
            filterOptionsColumns: !filterOptionsLoaded ? FILTER_OPTIONS_COLUMNS : null
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, filterOptionsLoaded]);

    const exportParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null
        };
    }, [activeFilters, sortField, sortOrder]);

    const { data, isLoading, isFetching } = useGetRepairSlipReportQuery(queryParams);

    useEffect(() => {
        if (!filterOptionsLoaded && data?.filterOptions) {
            setFilterOptions(data.filterOptions);
            setFilterOptionsLoaded(true);
        }
    }, [data, filterOptionsLoaded]);

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters((prev) => ({ ...prev, ...updatedFilters }));
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const initFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(DEFAULT_SORT_FIELD);
        setSortOrder(DEFAULT_SORT_ORDER);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;

        if (nextSortOrder !== 0 && !backendColumnMap[nextSortField]) return;

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const onPage = useCallback((event) => {
        setLazyParams({ first: event.first, rows: event.rows, page: event.page + 1 });
    }, []);

    return {
        items: Array.isArray(data?.items) ? data.items : [],
        totalCount: data?.totalCount ?? 0,
        isLoading: isLoading || isFetching,
        lazyParams,
        activeFilters,
        filterOptions,
        sortField,
        sortOrder,
        exportParams,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters
    };
};

export default useRepairSlipReport;
